import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebase';
import {
  updatePassword,
  updateEmail,
  signOut,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updateProfile,
} from 'firebase/auth';
import { ref, get, query, orderByChild, equalTo, update } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import MobileLayout from '../../components/mobile/MobileLayout';
import { usePlans } from '../../hooks/usePlans';

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */
interface UserData {
  name: string;
  email: string;
  phone: string;
  plan: string;
  createdAt: number;
  lastLogin?: number;
  paymentStatus?: string;
  nextBillingDate?: number;
  lastPaymentDate?: number;
  billingCycle?: string;
  notificationPreferences?: {
    emailNotifications: boolean;
    smsNotifications: boolean;
    marketingEmails: boolean;
  };
  company?: {
    name?: string;
    industry?: string;
    website?: string;
    email?: string;
    phone?: string;
  };
}

interface BotRecord {
  id: string;
  businessName?: string;
  botType?: string;
  status?: string;
  messageCount?: number;
  userId?: string;
}

/* ─────────────────────────────────────────────
   STYLE HELPERS
───────────────────────────────────────────── */
const cardStyle: React.CSSProperties = {
  borderRadius: 12,
  background: 'var(--bg-card)',
  border: '1px solid var(--card-border)',
  boxShadow: 'var(--card-shadow)',
  padding: 16,
  marginBottom: 16,
};

const sectionHeader: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--text-faint)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 8,
  marginTop: 24,
  fontWeight: 700,
};

const primaryBtn: React.CSSProperties = {
  background: '#5B4FE8',
  color: 'white',
  borderRadius: 9,
  padding: '12px 20px',
  fontSize: 14,
  fontWeight: 600,
  border: 'none',
  width: '100%',
  cursor: 'pointer',
  minHeight: 44,
};

const inputStyle: React.CSSProperties = {
  background: 'var(--bg-input)',
  border: '1px solid var(--card-border)',
  borderRadius: 8,
  padding: '10px 14px',
  color: 'var(--text-primary)',
  fontSize: 14,
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: 'var(--text-secondary)',
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 6,
};

/* ─────────────────────────────────────────────
   TOAST / MESSAGE BAR
───────────────────────────────────────────── */
function MessageBar({ message, onDismiss }: { message: { type: 'success' | 'error'; text: string } | null; onDismiss: () => void }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [message, onDismiss]);

  if (!message) return null;
  const isSuccess = message.type === 'success';
  return (
    <div style={{
      position: 'fixed', top: 60, left: 16, right: 16, zIndex: 200,
      background: isSuccess ? '#064e3b' : '#7f1d1d',
      border: `1px solid ${isSuccess ? '#10b98150' : '#dc262650'}`,
      borderRadius: 10, padding: '12px 16px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
      boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
    }}>
      <span style={{ color: isSuccess ? '#6ee7b7' : '#fca5a5', fontSize: 13, flex: 1 }}>{message.text}</span>
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, padding: 2, lineHeight: 1 }}>✕</button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   BOT ICON
───────────────────────────────────────────── */
function BotIcon({ type }: { type?: string }) {
  const icons: Record<string, string> = { whatsapp: '💬', facebook: '📘', instagram: '📸', website: '🌐' };
  return <span style={{ fontSize: 22 }}>{icons[type ?? ''] ?? '🤖'}</span>;
}

/* ─────────────────────────────────────────────
   STATUS BADGE
───────────────────────────────────────────── */
function BotStatusBadge({ status }: { status?: string }) {
  const cfg: Record<string, { bg: string; color: string; label: string }> = {
    active:    { bg: '#10b98122', color: '#10b981', label: 'Active' },
    inactive:  { bg: '#6b728022', color: '#9ca3af', label: 'Inactive' },
    suspended: { bg: '#dc262622', color: '#f87171', label: 'Suspended' },
    pending:   { bg: '#f59e0b22', color: '#fbbf24', label: 'Pending' },
  };
  const c = cfg[status ?? ''] ?? { bg: '#6b728022', color: '#9ca3af', label: status ?? 'Unknown' };
  return <span style={{ background: c.bg, color: c.color, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>{c.label}</span>;
}

/* ─────────────────────────────────────────────
   PLAN BADGE
───────────────────────────────────────────── */
function PlanBadge({ plan }: { plan: string }) {
  const colors: Record<string, string> = { free: '#6b7280', starter: '#6b7280', business: '#5B4FE8', agency: '#3b82f6', enterprise: '#f59e0b' };
  const color = colors[plan] ?? '#6b7280';
  return (
    <span style={{ background: color + '22', color, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, textTransform: 'capitalize' }}>
      {plan} plan
    </span>
  );
}

/* ─────────────────────────────────────────────
   TOGGLE ROW
───────────────────────────────────────────── */
function ToggleRow({ label, sublabel, checked, onChange }: { label: string; sublabel: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--bg-element)', minHeight: 52 }}>
      <div style={{ flex: 1 }}>
        <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 500 }}>{label}</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>{sublabel}</div>
      </div>
      <div
        onClick={() => onChange(!checked)}
        style={{ width: 44, height: 26, borderRadius: 13, background: checked ? '#5B4FE8' : 'var(--bg-element)', border: `1px solid ${checked ? '#4338ca' : 'var(--card-border)'}`, position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0, marginLeft: 12 }}
      >
        <div style={{ position: 'absolute', top: 3, left: checked ? 19 : 3, width: 18, height: 18, borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   USAGE BAR
───────────────────────────────────────────── */
function UsageBar({ label, used, limit, color = '#5B4FE8' }: { label: string; used: number; limit: number | null; color?: string }) {
  const pct = limit ? Math.min(100, (used / limit) * 100) : 0;
  const barColor = pct > 90 ? '#dc2626' : pct > 70 ? '#f59e0b' : color;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{label}</span>
        <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600 }}>
          {used} / {limit === null ? '∞' : limit}
        </span>
      </div>
      <div style={{ height: 6, background: 'var(--bg-element)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${limit === null ? 0 : pct}%`, background: barColor, borderRadius: 3, transition: 'width 0.5s ease' }} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   DELETE ACCOUNT MODAL
───────────────────────────────────────────── */
function DeleteModal({ currentPassword, onCurrentPasswordChange, onCancel, onConfirm, loading }: {
  currentPassword: string;
  onCurrentPasswordChange: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  const [confirmText, setConfirmText] = useState('');
  const canDelete = confirmText === 'DELETE' && currentPassword.length > 0;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: '20px 20px 0 0', padding: '24px 16px', width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>⚠️</div>
          <div style={{ color: '#f87171', fontWeight: 700, fontSize: 18, marginBottom: 6 }}>Delete Account</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>This action cannot be undone. All your bots, conversations, and billing data will be permanently deleted.</div>
        </div>

        <div style={{ background: '#dc262610', border: '1px solid #dc262630', borderRadius: 10, padding: 14, marginBottom: 18 }}>
          {['All your bots and configurations', 'All conversation history and analytics', 'Your billing information and invoices', 'Your account credentials'].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
              <span style={{ color: '#f87171' }}>✕</span> {item}
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Type <span style={{ color: '#f87171', fontWeight: 700 }}>DELETE</span> to confirm</label>
          <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="DELETE" style={{ ...inputStyle, border: confirmText === 'DELETE' ? '1px solid #10b981' : '1px solid var(--card-border)' }} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Your current password</label>
          <input type="password" value={currentPassword} onChange={e => onCurrentPasswordChange(e.target.value)} placeholder="Enter your password" style={inputStyle} />
        </div>

        <button
          onClick={onConfirm}
          disabled={!canDelete || loading}
          style={{ ...primaryBtn, background: canDelete && !loading ? '#dc2626' : 'var(--bg-element)', color: canDelete && !loading ? 'white' : 'var(--text-muted)', cursor: canDelete && !loading ? 'pointer' : 'not-allowed', marginBottom: 10 }}
        >
          {loading ? 'Deleting account…' : 'Permanently Delete Account'}
        </button>
        <button onClick={onCancel} style={{ width: '100%', padding: 12, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--card-border)', borderRadius: 9, cursor: 'pointer', fontSize: 14, minHeight: 44 }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function AccountMobile() {
  const navigate = useNavigate();
  const { plans, getBotLimit } = usePlans();

  const [user, setUser] = useState<any>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [bots, setBots] = useState<BotRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form states
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');

  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Notifications
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(true);
  const [marketingEmails, setMarketingEmails] = useState(false);

  // Company
  const [coName, setCoName] = useState('');
  const [coIndustry, setCoIndustry] = useState('');
  const [coWebsite, setCoWebsite] = useState('');
  const [coEmail, setCoEmail] = useState('');
  const [coPhone, setCoPhone] = useState('');

  // UI
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);

  // Section expand state
  const [openSection, setOpenSection] = useState<string | null>('profile');

  // Load user data
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (cu) => {
      if (!cu) { navigate('/login'); return; }
      setUser(cu);
      const snap = await get(ref(db, `users/${cu.uid}`));
      const data = snap.val() as UserData | null;
      setUserData(data);
      setDisplayName(data?.name || cu.displayName || '');
      setPhone(data?.phone || '');
      setEmailNotifications(data?.notificationPreferences?.emailNotifications ?? true);
      setSmsNotifications(data?.notificationPreferences?.smsNotifications ?? true);
      setMarketingEmails(data?.notificationPreferences?.marketingEmails ?? false);
      setCoName(data?.company?.name || '');
      setCoIndustry(data?.company?.industry || '');
      setCoWebsite(data?.company?.website || '');
      setCoEmail(data?.company?.email || '');
      setCoPhone(data?.company?.phone || '');

      const botsQuery = query(ref(db, 'bots'), orderByChild('userId'), equalTo(cu.uid));
      const botsSnap = await get(botsQuery);
      const list: BotRecord[] = [];
      botsSnap.forEach((c) => { list.push({ id: c.key!, ...c.val() }); });
      setBots(list);
    });
    return unsub;
  }, [navigate]);

  const showMsg = (type: 'success' | 'error', text: string) => setMessage({ type, text });

  // Update profile
  const handleUpdateProfile = async () => {
    setMessage(null);
    setLoading(true);
    try {
      if (user && displayName !== user.displayName) {
        await updateProfile(user, { displayName });
      }
      await update(ref(db, `users/${user.uid}`), { name: displayName, phone, updatedAt: Date.now() });
      showMsg('success', 'Profile updated successfully!');
    } catch (err: any) {
      showMsg('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    setMessage(null);
    if (newPassword !== confirmPassword) { showMsg('error', 'New passwords do not match!'); return; }
    if (newPassword.length < 6) { showMsg('error', 'Password must be at least 6 characters!'); return; }
    setLoading(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showMsg('success', 'Password changed successfully!');
    } catch (err: any) {
      if (err.code === 'auth/wrong-password') showMsg('error', 'Current password is incorrect!');
      else showMsg('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  // Send email verification
  const handleSendVerification = async () => {
    setLoading(true);
    try {
      // Sent via our own SMTP (server-side), not Firebase Auth's built-in
      // email delivery — see /api/auth/request-email-verification.
      const res = await apiFetch('/api/auth/request-email-verification', { method: 'POST' });
      if (!res.ok) throw new Error('Failed to send verification email. Please try again.');
      setVerificationSent(true);
      showMsg('success', 'Verification email sent! Please check your inbox.');
    } catch (err: any) {
      showMsg('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update notifications
  const handleUpdateNotifications = async () => {
    setLoading(true);
    try {
      await update(ref(db, `users/${user.uid}`), {
        notificationPreferences: { emailNotifications, smsNotifications, marketingEmails },
        updatedAt: Date.now(),
      });
      showMsg('success', 'Notification preferences updated!');
    } catch (err: any) {
      showMsg('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update company
  const handleUpdateCompany = async () => {
    setLoading(true);
    try {
      await update(ref(db, `users/${user.uid}`), {
        company: { name: coName, industry: coIndustry, website: coWebsite, email: coEmail, phone: coPhone, updatedAt: Date.now() },
        updatedAt: Date.now(),
      });
      showMsg('success', 'Company details updated!');
    } catch (err: any) {
      showMsg('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete account
  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, deletePassword);
      await reauthenticateWithCredential(user, credential);
      const res = await apiFetch('/api/account', { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Deletion failed');
      await signOut(auth);
      navigate('/');
    } catch (err: any) {
      if (err.code === 'auth/wrong-password') showMsg('error', 'Password is incorrect!');
      else if (err.code === 'auth/requires-recent-login') showMsg('error', 'Please log out and log back in before deleting.');
      else showMsg('error', err.message);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  // Derived
  const botLimit = getBotLimit(userData?.plan ?? 'free');
  const activeBots = bots.filter(b => b.status === 'active').length;
  const currentPlanData = plans.find(p => p.id === userData?.plan);
  const nextBilling = userData?.lastPaymentDate
    ? new Date(userData.lastPaymentDate + (userData.billingCycle === 'yearly' ? 365 : 30) * 86400000)
    : null;

  const toggleSection = (s: string) => setOpenSection(prev => prev === s ? null : s);

  /* ────────────────── RENDER ────────────────── */
  return (
    <MobileLayout title="Account">
      <MessageBar message={message} onDismiss={() => setMessage(null)} />

      <div style={{ padding: '0 16px' }}>

        {/* Email verification banner */}
        {user && !user.emailVerified && (
          <div style={{ marginTop: 16, padding: '12px 14px', background: '#fef3c710', border: '1px solid #f59e0b30', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <span style={{ fontSize: 16, marginRight: 6 }}>⚠️</span>
              <span style={{ color: '#fbbf24', fontWeight: 600, fontSize: 13 }}>Email not verified</span>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>Some features may be limited.</div>
            </div>
            <button onClick={handleSendVerification} disabled={verificationSent} style={{ padding: '8px 16px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, cursor: verificationSent ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', minHeight: 36 }}>
              {verificationSent ? 'Sent!' : 'Verify Email'}
            </button>
          </div>
        )}

        {/* ── Profile ── */}
        <p style={sectionHeader}>Profile</p>
        <div style={cardStyle}>
          {/* Avatar + name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg,#5B4FE8,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: 'white', flexShrink: 0 }}>
              {(userData?.name || user?.displayName || user?.email || '?')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 16 }}>{userData?.name || user?.displayName || 'User'}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>{user?.email}</div>
              {userData?.plan && <div style={{ marginTop: 6 }}><PlanBadge plan={userData.plan} /></div>}
            </div>
          </div>

          {/* Editable fields */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Display Name</label>
            <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Your name" style={inputStyle} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Phone Number</label>
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+263712345678" style={inputStyle} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Email Address</label>
            <input type="email" value={user?.email || ''} readOnly style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }} />
            {user?.emailVerified && <div style={{ fontSize: 11, color: '#10b981', marginTop: 4 }}>✓ Email verified</div>}
          </div>

          <button onClick={handleUpdateProfile} disabled={loading} style={{ ...primaryBtn, opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Saving…' : 'Save Profile'}
          </button>
        </div>

        {/* ── Plan & billing shortcut ── */}
        <p style={sectionHeader}>Plan & Billing</p>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 16 }}>{currentPlanData?.name ?? (userData?.plan ?? 'Free')} Plan</div>
              {nextBilling && (
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
                  Renews {nextBilling.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              )}
              {userData?.paymentStatus && (
                <div style={{ marginTop: 6 }}>
                  <span style={{
                    background: userData.paymentStatus === 'active' ? '#10b98122' : userData.paymentStatus === 'trial' ? '#a855f722' : '#f59e0b22',
                    color: userData.paymentStatus === 'active' ? '#10b981' : userData.paymentStatus === 'trial' ? '#a855f7' : '#fbbf24',
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, textTransform: 'capitalize',
                  }}>
                    {userData.paymentStatus.replace('_', ' ')}
                  </span>
                </div>
              )}
            </div>
            <span style={{ fontSize: 28 }}>{currentPlanData?.icon ?? '💼'}</span>
          </div>

          {/* Usage bars */}
          <UsageBar label="Bots" used={bots.length} limit={currentPlanData?.botLimit ?? null} />

          <button onClick={() => navigate('/billing')} style={{ ...primaryBtn, background: 'linear-gradient(135deg,#5B4FE8,#7c3aed)' }}>
            Manage Billing
          </button>
        </div>

        {/* ── Bots ── */}
        {bots.length > 0 && (
          <>
            <p style={sectionHeader}>Your Bots</p>

            {/* Stats row */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              {[
                { icon: '🤖', label: 'Total', value: bots.length },
                { icon: '🟢', label: 'Active', value: activeBots },
                { icon: '💬', label: 'WhatsApp', value: bots.filter(b => b.botType === 'whatsapp').length },
                { icon: '📘', label: 'Facebook', value: bots.filter(b => b.botType === 'facebook').length },
              ].map(stat => (
                <div key={stat.label} style={{ flex: 1, background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '10px 6px', textAlign: 'center' }}>
                  <div style={{ fontSize: 18 }}>{stat.icon}</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 15 }}>{stat.value}</div>
                  <div style={{ color: 'var(--text-faint)', fontSize: 10 }}>{stat.label}</div>
                </div>
              ))}
            </div>

            {bots.map(bot => (
              <div key={bot.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
                <div style={{ width: 44, height: 44, background: 'var(--bg-element)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <BotIcon type={bot.botType} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {bot.businessName || 'Unnamed Bot'}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2, textTransform: 'capitalize' }}>
                    {bot.botType ?? 'bot'}
                    {bot.messageCount !== undefined && bot.messageCount > 0 && ` · ${bot.messageCount.toLocaleString()} msgs`}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
                  <BotStatusBadge status={bot.status} />
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate('/dashboard'); }}
                    style={{ background: 'var(--bg-element)', border: '1px solid var(--card-border)', color: 'var(--text-secondary)', borderRadius: 7, padding: '4px 10px', fontSize: 12, cursor: 'pointer', minHeight: 28 }}
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* ── Password ── */}
        <p style={sectionHeader}>Password</p>
        <div style={cardStyle}>
          <button
            onClick={() => toggleSection('password')}
            style={{ width: '100%', background: 'none', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 0, cursor: 'pointer', minHeight: 44 }}
          >
            <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 15 }}>Change Password</div>
            <span style={{ color: 'var(--text-muted)', fontSize: 18 }}>{openSection === 'password' ? '▲' : '▼'}</span>
          </button>

          {openSection === 'password' && (
            <div style={{ marginTop: 16, borderTop: '1px solid var(--bg-element)', paddingTop: 16 }}>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Current Password</label>
                <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Current password" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>New Password</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password (min 6 characters)" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  style={{ ...inputStyle, border: confirmPassword && confirmPassword !== newPassword ? '1px solid #dc2626' : '1px solid var(--card-border)' }}
                />
                {confirmPassword && confirmPassword !== newPassword && (
                  <div style={{ color: '#f87171', fontSize: 12, marginTop: 4 }}>Passwords do not match</div>
                )}
              </div>
              <button
                onClick={handleChangePassword}
                disabled={loading || !currentPassword || !newPassword || newPassword !== confirmPassword}
                style={{ ...primaryBtn, opacity: loading || !currentPassword || !newPassword || newPassword !== confirmPassword ? 0.6 : 1, cursor: loading || !currentPassword || !newPassword || newPassword !== confirmPassword ? 'not-allowed' : 'pointer' }}
              >
                {loading ? 'Updating…' : 'Update Password'}
              </button>
            </div>
          )}
        </div>

        {/* ── Company details ── */}
        <p style={sectionHeader}>Company</p>
        <div style={cardStyle}>
          <button
            onClick={() => toggleSection('company')}
            style={{ width: '100%', background: 'none', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 0, cursor: 'pointer', minHeight: 44 }}
          >
            <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 15 }}>Company Details</div>
            <span style={{ color: 'var(--text-muted)', fontSize: 18 }}>{openSection === 'company' ? '▲' : '▼'}</span>
          </button>

          {openSection === 'company' && (
            <div style={{ marginTop: 16, borderTop: '1px solid var(--bg-element)', paddingTop: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>This information appears on invoices and business communications.</div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Company / Business Name</label>
                <input type="text" value={coName} onChange={e => setCoName(e.target.value)} placeholder="Acme Corp" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Industry</label>
                <select value={coIndustry} onChange={e => setCoIndustry(e.target.value)} style={{ ...inputStyle, background: 'var(--bg-input)' }}>
                  <option value="">Select industry…</option>
                  {['Retail', 'Food & Beverage', 'Healthcare', 'Real Estate', 'Education', 'Finance', 'Transport & Logistics', 'Hospitality', 'Agriculture', 'Construction', 'Technology', 'Media & Entertainment', 'Professional Services', 'Other'].map(i => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Website</label>
                <input type="url" value={coWebsite} onChange={e => setCoWebsite(e.target.value)} placeholder="https://yourcompany.com" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Business Email</label>
                <input type="email" value={coEmail} onChange={e => setCoEmail(e.target.value)} placeholder="info@company.com" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Business Phone</label>
                <input type="tel" value={coPhone} onChange={e => setCoPhone(e.target.value)} placeholder="+263712345678" style={inputStyle} />
              </div>
              <button onClick={handleUpdateCompany} disabled={loading} style={{ ...primaryBtn, opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Saving…' : 'Save Company Details'}
              </button>
            </div>
          )}
        </div>

        {/* ── Notifications ── */}
        <p style={sectionHeader}>Notifications</p>
        <div style={cardStyle}>
          <button
            onClick={() => toggleSection('notifications')}
            style={{ width: '100%', background: 'none', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 0, cursor: 'pointer', minHeight: 44 }}
          >
            <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 15 }}>Notification Preferences</div>
            <span style={{ color: 'var(--text-muted)', fontSize: 18 }}>{openSection === 'notifications' ? '▲' : '▼'}</span>
          </button>

          {openSection === 'notifications' && (
            <div style={{ marginTop: 12, borderTop: '1px solid var(--bg-element)', paddingTop: 4 }}>
              <ToggleRow
                label="Email Notifications"
                sublabel="Important updates about your account and bots"
                checked={emailNotifications}
                onChange={setEmailNotifications}
              />
              <ToggleRow
                label="SMS Notifications"
                sublabel="Text messages for important alerts"
                checked={smsNotifications}
                onChange={setSmsNotifications}
              />
              <ToggleRow
                label="Marketing Emails"
                sublabel="Tips, updates, and special offers"
                checked={marketingEmails}
                onChange={setMarketingEmails}
              />
              <button onClick={handleUpdateNotifications} disabled={loading} style={{ ...primaryBtn, marginTop: 16, opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Saving…' : 'Save Preferences'}
              </button>
            </div>
          )}
        </div>

        {/* ── Logout ── */}
        <button
          onClick={handleLogout}
          style={{ width: '100%', padding: '13px 20px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--card-border)', borderRadius: 9, cursor: 'pointer', fontSize: 15, fontWeight: 600, minHeight: 48, marginBottom: 16 }}
        >
          Log Out
        </button>

        {/* ── Danger zone ── */}
        <p style={{ ...sectionHeader, color: '#f87171' }}>Danger Zone</p>
        <div style={{ ...cardStyle, border: '1px solid #dc262633', background: '#dc262608', marginBottom: 32 }}>
          <div style={{ color: '#f87171', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Delete Account</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 14 }}>
            Permanently delete your account and all associated data. This cannot be undone.
          </div>
          <button
            onClick={() => setShowDeleteModal(true)}
            style={{ width: '100%', padding: '12px 20px', background: 'transparent', color: '#f87171', border: '1px solid #dc2626', borderRadius: 9, cursor: 'pointer', fontSize: 14, fontWeight: 600, minHeight: 44 }}
          >
            Delete My Account
          </button>
        </div>

      </div>

      {/* Delete account modal */}
      {showDeleteModal && (
        <DeleteModal
          currentPassword={deletePassword}
          onCurrentPasswordChange={setDeletePassword}
          onCancel={() => { setShowDeleteModal(false); setDeletePassword(''); }}
          onConfirm={handleDeleteAccount}
          loading={loading}
        />
      )}
    </MobileLayout>
  );
}
